import React, { type ReactNode } from "react";
import { Typography, Space } from "antd";

import {
  TYPE_NONE,
  TYPE_PHONE,
  // TYPE_DOMAIN,
  TYPE_UPLOAD,
  TYPE_HIDDEN,
  TYPE_REDACTOR,
} from "@components/Form";
import { getOrganizationFooterHtml } from "@components/Organization";

import getLinkTemplatesSchema from "./getLinkTemplatesSchema";

const { Text } = Typography;

const LABEL_LOGO = "Logo";
const LABEL_PHONE = "Phone";
const LABEL_UPLOAD = "Upload Logo";
const LABEL_FORMATS = "SVG and PNG formats supported";
const LABEL_SIGNATURE = "Email Signature";
const LABEL_DOCUSIGN_ACCOUNT_ID = "DocuSign Account ID";
const LABEL_DOCUSIGN_WEBHOOK_SECRET = "DocuSign Webhook Secret";

type Organization = {
  domain?: string;
  emailFooterSource?: string;
  isEmailVerified?: boolean;
};

type InputItem = {
  name: string;
  type?: string;
  required?: boolean;
  label?: ReactNode;
  initialValue?: string;
  schema?: InputItem[];
  itemProps?: {
    extra?: ReactNode;
  };
  inputProps?: {
    disabled?: boolean;
    isPublic?: boolean;
    fileTypes?: string[];
    actionTitle?: string;
    emailTargetPath?: string;
  };
};

const getUpdateInputs = (
  organization: Organization,
  onlyFieldNames: string[] = [],
): InputItem[] => {
  // const disabledDomainValue = organization?.domainStatus === "COMPLETE"

  const emailFooterSourceInitialValue =
    organization.emailFooterSource || getOrganizationFooterHtml(organization);

  const inputs: InputItem[] = [
    {
      name: "senderSourceId",
      type: TYPE_NONE,
    },
    {
      name: "docusignAccountId",
      type: TYPE_NONE,
      label: LABEL_DOCUSIGN_ACCOUNT_ID,
      required: true,
    },
    {
      name: "docusignWebhookSecret",
      type: TYPE_NONE,
      label: LABEL_DOCUSIGN_WEBHOOK_SECRET,
      required: true,
    },
    {
      name: "name",
    },
    {
      name: "domain",
      type: TYPE_NONE,
      // type: TYPE_DOMAIN,
      // inputProps: {
      //   disabled: disabledDomainValue,
      // }
    },
    {
      name: "email",
      inputProps: {
        disabled: organization.isEmailVerified,
      },
    },
    {
      name: "phoneNumber",
      type: TYPE_PHONE,
      label: LABEL_PHONE,
    },
    {
      name: "logoUrl",
      type: TYPE_UPLOAD,
      label: (
        <Space>
          <Text strong>{LABEL_LOGO}</Text>
          <Text type="secondary">({LABEL_FORMATS})</Text>
        </Space>
      ),
      inputProps: {
        isPublic: true,
        fileTypes: [".svg", ".png"],
        actionTitle: LABEL_UPLOAD,
      },
    },
    {
      name: "emailFooterSource",
      label: LABEL_SIGNATURE,
      type: TYPE_REDACTOR,
      initialValue: emailFooterSourceInitialValue,
      inputProps: {
        emailTargetPath: "emailFooterHtml",
      },
    },
    {
      name: "emailFooterHtml",
      type: TYPE_HIDDEN,
    },
    {
      // NOTE: hidden by default so the general Edit Organization form does
      //       not expose custom links; only rendered when requested via
      //       onlyFieldNames (Edit Custom Links modal), where the object
      //       type is taken from the operation's form inputs schema
      name: "linkTemplates",
      type: TYPE_NONE,
      label: "",
      schema: getLinkTemplatesSchema(organization.domain),
    },
  ];

  const hasOnlyFieldNames = onlyFieldNames.length > 0;

  if (!hasOnlyFieldNames) {
    return inputs;
  }

  const onlyInputs = inputs.map((input) => {
    const { name, type, ...otherProps } = input;
    const isNone = !onlyFieldNames.includes(name);

    if (isNone) {
      return {
        name,
        type: TYPE_NONE,
      };
    }

    return { name, ...otherProps };
  });

  return onlyInputs;
};

export default getUpdateInputs;
